import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { UtensilsCrossed, ExternalLink, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const badgeStyle = {
  'Excellent Match': 'bg-green-900/60 text-green-300 border-green-700',
  'Good Match': 'bg-amber-900/60 text-amber-300 border-amber-700',
  'Fair Match': 'bg-stone-800 text-stone-400 border-stone-600',
};

export const MealsThatFit = ({ selectedDate, isPremium, refreshKey }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isPremium) return;
    axios.get(`${API}/meals/recommendations?mode=today&date=${selectedDate}`)
      .then(res => setData(res.data))
      .catch(() => setData(null));
  }, [isPremium, selectedDate, refreshKey]);

  const remaining = data?.remaining?.calories ?? 0;
  const meals = (data?.meals || []).filter(m => m.match_label !== 'Fair Match').slice(0, 3);
  if (!isPremium || !data || remaining < 400 || meals.length === 0) return null;

  const isEvening = new Date().getHours() >= 17;

  const handleOrder = (e, meal) => {
    e.stopPropagation();
    if (!meal.affiliate_url) return;
    axios.post(`${API}/meals/track-click`, {
      provider_id: meal.provider_id, meal_id: meal.id,
      source: 'nutrition_widget', mode: 'today',
      platform: navigator.userAgent.includes('Mobi') ? 'mobile' : 'desktop',
    }).catch(() => {});
    toast.info(`Opening ${meal.provider_name} — you're leaving Anvil & Ash`);
    window.open(meal.affiliate_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="glass p-5 border border-stone-800" data-testid="meals-that-fit-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-blood-400 tracking-wider flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5" />
          MEALS THAT FIT {isEvening ? 'TONIGHT' : 'TODAY'}
        </h2>
        <button onClick={() => navigate('/prepared-meals')} className="text-stone-400 text-xs hover:text-white flex items-center gap-0.5" data-testid="meals-that-fit-see-all">
          See all <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <p className="text-stone-500 text-xs mb-3">
        You have <b className="text-stone-300">{remaining} calories</b> left — these prepared meals fit your remaining macros.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {meals.map(meal => (
          <button key={meal.id} onClick={() => navigate('/prepared-meals')}
            className="glass border border-stone-800 hover:border-blood-800 rounded-lg overflow-hidden text-left transition-colors"
            data-testid={`fit-meal-${meal.id}`}>
            <div className="relative h-20">
              {meal.image_url && <img src={meal.image_url} alt={meal.name} className="w-full h-full object-cover" loading="lazy" />}
              <span className={`absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${badgeStyle[meal.match_label]}`}>
                {meal.match_label}
              </span>
            </div>
            <div className="p-2.5">
              <p className="text-stone-500 text-[10px] uppercase tracking-wider truncate">{meal.provider_name}</p>
              <p className="text-white text-xs font-semibold leading-snug truncate">{meal.name}</p>
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-stone-400 text-[11px]">{meal.calories} cal • {meal.protein}g protein</p>
                <span onClick={(e) => handleOrder(e, meal)}
                  className="text-blood-400 text-[11px] font-bold hover:text-blood-300 flex items-center gap-0.5"
                  data-testid={`fit-meal-order-${meal.id}`}>
                  Order <ExternalLink className="w-2.5 h-2.5" />
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </Card>
  );
};

export default MealsThatFit;
